import React from 'react'
import { EnvironmentOutlined } from '@ant-design/icons'
import { Alert, Button, Select, Spin, Typography } from 'antd'
import debounce from 'lodash.debounce'
import cdekMarkerIcon from 'assets/images/map-marker-cdek.svg'
import fivePostMarkerIcon from 'assets/images/map-marker-5post.svg'
import yandexMarkerIcon from 'assets/images/map-marker-yandex.svg'
import { appApi } from 'api/app'
import { pickupPointsApi } from 'api/pickupPoints'
import { yandexDeliveryApi } from 'api/yandexDelivery'
import { IAddressSuggestion } from 'models/app'
import {
  IPickupPoint,
  IPickupPointBounds,
  TPickupPointProvider
} from 'models/pickupPoint'
import { DEBOUNCE_DELAY } from 'shared/constants/app'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { loadYandexMaps } from 'shared/utils/yandexMaps'
import styles from './Logistics.module.scss'

type TCoordinates = [number, number]

interface IDeliveryPickupPointSelectorProps {
  value: IPickupPoint | null
  onChange: (point: IPickupPoint | null) => void
  weightMaxKg?: number
  mapId?: string
  provider?: TPickupPointProvider
  allowedProviders?: TPickupPointProvider[]
  cityPlaceholder?: string
  ariaLabel?: string
  pointPurpose?: 'pickup' | 'dropoff'
}

interface IMapEvent {
  get: (name: string) => unknown
}

interface IYandexMap {
  destroy: () => void
  getBounds: () => number[][]
  getZoom: () => number
  setCenter: (
    center: TCoordinates,
    zoom: number,
    options?: Record<string, unknown>
  ) => Promise<unknown>
  events: { add: (name: string, handler: () => void) => void }
  geoObjects: { add: (object: unknown) => void }
}

interface IYandexObjectManager {
  add: (features: unknown[]) => void
  removeAll: () => void
  clusters: { options: { set: (name: string, value: unknown) => void } }
  objects: {
    events: {
      add: (name: string, handler: (event: IMapEvent) => void) => void
    }
  }
}

interface ICityOption {
  name: string
  latitude: number
  longitude: number
}

interface IViewport extends IPickupPointBounds {
  zoom: number
}

interface ICacheEntry {
  bounds: IPickupPointBounds
  zoomLevel: number
  weightMaxKg?: number
  provider?: TPickupPointProvider
  allowedProviderKey?: string
  pointPurpose: 'pickup' | 'dropoff'
  points: IPickupPoint[]
}

const DEFAULT_CENTER: TCoordinates = [55.76, 37.64]
const DEFAULT_ZOOM = 11
const POINTS_CACHE_SIZE = 8
const BOUNDS_PADDING_RATIO = 0.2

const getPointId = (point: IPickupPoint) =>
  `${point.provider}:${point.code || point.uuid}`

export const isFivePostPickupPoint = (point: IPickupPoint): boolean =>
  point.provider === 'YANDEX' && /\b5\s*post\b|пят[её]роч/i.test(point.name)

export const getPickupPointProviderName = (point: IPickupPoint): string => {
  if (isFivePostPickupPoint(point)) return '5Post'
  return point.provider === 'YANDEX' ? 'Яндекс' : 'СДЭК'
}

const getPointIcon = (point: IPickupPoint) => {
  if (isFivePostPickupPoint(point)) return fivePostMarkerIcon
  return point.provider === 'YANDEX' ? yandexMarkerIcon : cdekMarkerIcon
}

const getAddress = (point: IPickupPoint) =>
  point.location.address_full ?? point.location.address ?? point.name

const getViewport = (map: IYandexMap): IViewport | null => {
  const bounds = map.getBounds()
  if (!Array.isArray(bounds) || bounds.length < 2) return null
  const latitudes = bounds.map((point) => point[0])
  const longitudes = bounds.map((point) => point[1])
  return {
    latitude: {
      from: Math.min(...latitudes),
      to: Math.max(...latitudes)
    },
    longitude: {
      from: Math.min(...longitudes),
      to: Math.max(...longitudes)
    },
    zoom: map.getZoom()
  }
}

const expandBounds = (viewport: IViewport): IPickupPointBounds => {
  const latitudePadding =
    (viewport.latitude.to - viewport.latitude.from) * BOUNDS_PADDING_RATIO
  const longitudePadding =
    (viewport.longitude.to - viewport.longitude.from) * BOUNDS_PADDING_RATIO

  return {
    latitude: {
      from: viewport.latitude.from - latitudePadding,
      to: viewport.latitude.to + latitudePadding
    },
    longitude: {
      from: viewport.longitude.from - longitudePadding,
      to: viewport.longitude.to + longitudePadding
    }
  }
}

const isViewportCovered = (
  viewport: IViewport,
  bounds: IPickupPointBounds
) =>
  bounds.latitude.from <= viewport.latitude.from &&
  bounds.latitude.to >= viewport.latitude.to &&
  bounds.longitude.from <= viewport.longitude.from &&
  bounds.longitude.to >= viewport.longitude.to

const getPointLimit = (_zoom: number) => {
  return 40
}

export const DeliveryPickupPointSelector: React.FC<
  IDeliveryPickupPointSelectorProps
> = ({
  value,
  onChange,
  weightMaxKg,
  mapId = 'delivery-calculator-pvz-map',
  provider,
  allowedProviders,
  cityPlaceholder = 'Введите город получателя',
  ariaLabel = 'Выбор пункта выдачи получателя',
  pointPurpose = 'pickup'
}) => {
  const allowedProviderKey = allowedProviders === undefined
    ? undefined
    : [...allowedProviders].sort().join('|')
  const mapRef = React.useRef<IYandexMap | null>(null)
  const objectManagerRef = React.useRef<IYandexObjectManager | null>(null)
  const pointsRef = React.useRef(new Map<string, IPickupPoint>())
  const cacheRef = React.useRef<ICacheEntry[]>([])
  const requestIdRef = React.useRef(0)
  const onChangeRef = React.useRef(onChange)
  const [isReady, setIsReady] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [hasLoaded, setHasLoaded] = React.useState(false)
  const [pointsCount, setPointsCount] = React.useState(0)
  const [cities, setCities] = React.useState<IAddressSuggestion[]>([])
  const [selectedCity, setSelectedCity] = React.useState<ICityOption | null>(null)

  React.useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const renderPoints = React.useCallback((points: IPickupPoint[]) => {
    if (!objectManagerRef.current) return
    const unique = new Map<string, IPickupPoint>()
    points.forEach((point) => unique.set(getPointId(point), point))
    pointsRef.current = unique
    objectManagerRef.current.removeAll()
    objectManagerRef.current.add(
      Array.from(unique.entries()).map(([id, point]) => ({
        id,
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [point.location.latitude, point.location.longitude]
        },
        properties: {
          hintContent: `${getPickupPointProviderName(point)} — ${getAddress(
            point
          )}`
        },
        options: {
          iconLayout: 'default#image',
          iconImageHref: getPointIcon(point),
          iconImageSize: [39, 49],
          iconImageOffset: [-19, -49]
        }
      }))
    )
    setPointsCount(unique.size)
    setHasLoaded(true)
  }, [])

  const loadPoints = React.useCallback(async () => {
    const map = mapRef.current
    if (!map) return
    const viewport = getViewport(map)
    if (!viewport) return
    const zoomLevel = Math.round(viewport.zoom)
    const cachedIndex = cacheRef.current.findIndex(
      (entry) =>
        entry.zoomLevel === zoomLevel &&
        entry.weightMaxKg === weightMaxKg &&
        entry.provider === provider &&
        entry.allowedProviderKey === allowedProviderKey &&
        entry.pointPurpose === pointPurpose &&
        isViewportCovered(viewport, entry.bounds)
    )
    if (cachedIndex >= 0) {
      const [cached] = cacheRef.current.splice(cachedIndex, 1)
      cacheRef.current.push(cached)
      renderPoints(cached.points)
      return
    }

    const bounds = expandBounds(viewport)
    const requestId = ++requestIdRef.current
    setIsLoading(true)
    try {
      const acceptsProvider = (candidate: TPickupPointProvider) =>
        (!provider || provider === candidate) &&
        (allowedProviderKey === undefined ||
          allowedProviderKey.split('|').includes(candidate))

      let points: IPickupPoint[]
      if (pointPurpose === 'dropoff') {
        const [pickupResponse, dropoffResponse] = await Promise.all([
          acceptsProvider('CDEK')
            ? pickupPointsApi.getPickupPoints({
              ...bounds,
              limit: getPointLimit(viewport.zoom),
              selection_strategy: 'grid',
              ...(weightMaxKg && { weight_max: weightMaxKg })
            })
            : Promise.resolve(null),
          acceptsProvider('YANDEX')
            ? yandexDeliveryApi.getDropoffStations({
              ...bounds,
              available_for_dropoff: true
            })
            : Promise.resolve(null)
        ])
        points = [
          ...(pickupResponse?.data.cdek ?? []),
          ...(dropoffResponse?.data ?? []).map((station) => ({
            provider: 'YANDEX' as const,
            code: station.code || station.uuid,
            uuid: station.uuid,
            name: station.name,
            location: station.location,
            work_time: station.work_time
          }))
        ]
      } else {
        const { data } = await pickupPointsApi.getPickupPoints({
          ...bounds,
          limit: getPointLimit(viewport.zoom),
          selection_strategy: 'grid',
          ...(weightMaxKg && { weight_max: weightMaxKg })
        })
        points = [...data.cdek, ...data.yandex].filter((point) =>
          acceptsProvider(point.provider)
        )
      }
      if (requestId === requestIdRef.current) {
        cacheRef.current.push({
          bounds,
          zoomLevel,
          weightMaxKg,
          provider,
          allowedProviderKey,
          pointPurpose,
          points
        })
        if (cacheRef.current.length > POINTS_CACHE_SIZE) {
          cacheRef.current.shift()
        }
        renderPoints(points)
      }
    } catch (error) {
      if (requestId === requestIdRef.current) showServerError(error)
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false)
    }
  }, [allowedProviderKey, pointPurpose, provider, renderPoints, weightMaxKg])

  const loadPointsRef = React.useRef(loadPoints)
  React.useEffect(() => {
    loadPointsRef.current = loadPoints
  }, [loadPoints])

  React.useEffect(() => {
    let destroyed = false
    let debouncedLoad: ReturnType<typeof debounce> | null = null
    const init = async () => {
      try {
        await loadYandexMaps()
        if (destroyed) return
        if (!window.ymaps) {
          showMessage('error', 'Не удалось загрузить карту', 'Обновите страницу')
          return
        }
        window.ymaps.ready(() => {
          if (destroyed || mapRef.current) return
          const map = new window.ymaps.Map(mapId, {
            center: DEFAULT_CENTER,
            zoom: DEFAULT_ZOOM,
            controls: ['geolocationControl', 'zoomControl', 'fullscreenControl']
          }) as IYandexMap
          const manager = new window.ymaps.ObjectManager({
            clusterize: true,
            gridSize: 96
          }) as IYandexObjectManager
          manager.clusters.options.set(
            'preset',
            'islands#invertedGrayClusterIcons'
          )
          manager.objects.events.add('click', (event) => {
            const point = pointsRef.current.get(
              String(event.get('objectId') ?? '')
            )
            if (point) onChangeRef.current(point)
          })
          map.geoObjects.add(manager)
          mapRef.current = map
          objectManagerRef.current = manager
          debouncedLoad = debounce(() => loadPointsRef.current(), 300)
          map.events.add('boundschange', debouncedLoad)
          setIsReady(true)
        })
      } catch (error) {
        showMessage('error', 'Не удалось загрузить карту', 'Обновите страницу')
        showServerError(error)
      }
    }
    init()
    return () => {
      destroyed = true
      requestIdRef.current += 1
      debouncedLoad?.cancel()
      mapRef.current?.destroy()
      mapRef.current = null
      objectManagerRef.current = null
    }
  }, [])

  React.useEffect(() => {
    if (isReady) loadPoints()
  }, [isReady, loadPoints])

  React.useEffect(() => {
    if (!selectedCity || !isReady || !mapRef.current) return
    onChangeRef.current(null)
    void mapRef.current
      .setCenter(
        [selectedCity.latitude, selectedCity.longitude],
        DEFAULT_ZOOM,
        { duration: 300 }
      )
      .then(() => loadPointsRef.current())
  }, [isReady, selectedCity])

  const getCities = React.useCallback(async (search: string) => {
    if (!search.trim()) return setCities([])
    try {
      const { data } = await appApi.getAddresses({
        address: search,
        isCityOnly: 'true'
      })
      setCities(data.suggestions)
    } catch (error) {
      showServerError(error)
    }
  }, [])
  const debouncedGetCities = React.useMemo(
    () => debounce(getCities, DEBOUNCE_DELAY),
    [getCities]
  )
  React.useEffect(() => () => debouncedGetCities.cancel(), [debouncedGetCities])

  return (
    <div>
      <div className={styles.zoneMapControls}>
        <Select
          showSearch
          value={selectedCity?.name}
          placeholder={cityPlaceholder}
          notFoundContent={null}
          filterOption={false}
          className={styles.zoneCitySelect}
          onSearch={debouncedGetCities}
          onSelect={(raw: string) =>
            setSelectedCity(JSON.parse(raw) as ICityOption)
          }
          options={cities.map((item) => ({
            value: JSON.stringify({
              name: item.value,
              latitude: Number(item.data.geo_lat),
              longitude: Number(item.data.geo_lon)
            } satisfies ICityOption),
            label: item.value
          }))}
        />
        <div className={styles.providerLegend} aria-label='Перевозчики ПВЗ'>
          {[
            ['СДЭК', cdekMarkerIcon],
            ['Яндекс', yandexMarkerIcon],
            ['5Post', fivePostMarkerIcon]
          ].filter(([name]) =>
            (!provider ||
              (provider === 'CDEK' ? name === 'СДЭК' : name !== 'СДЭК')) &&
            (allowedProviderKey === undefined ||
              allowedProviderKey.split('|').includes(
                name === 'СДЭК' ? 'CDEK' : 'YANDEX'
              ))
          ).map(([name, icon]) => (
            <span key={name}>
              <img src={icon} alt='' />
              {name}
            </span>
          ))}
        </div>
      </div>
      <div className={`${styles.zoneMapWrapper} ${styles.calculatorMap}`}>
        <div
          id={mapId}
          className={styles.zoneMap}
          role='region'
          aria-label={ariaLabel}
        />
        {(!isReady || isLoading) && (
          <div className={styles.zoneMapLoading}><Spin /></div>
        )}
      </div>
      {hasLoaded && pointsCount === 0 && !isLoading && (
        <Alert
          className={styles.zoneMapEmpty}
          type='info'
          showIcon
          message='В этой области пунктов выдачи нет'
        />
      )}
      {value && (
        <div className={styles.selectedZonePoint}>
          <EnvironmentOutlined className={styles.selectedZonePointIcon} />
          <div>
            <Typography.Text strong>
              {getPickupPointProviderName(value)} — {value.name}
            </Typography.Text>
            <Typography.Text type='secondary'>{getAddress(value)}</Typography.Text>
          </div>
          <Button onClick={() => onChange(null)}>Изменить ПВЗ</Button>
        </div>
      )}
    </div>
  )
}
